import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { useEffect, useState } from "react";

import { getPromocoes, type PromocaoDoWidget, type TextosDoWidget } from "../../promocoesHandler";
import { janelaParaApostador } from "../../janelaPromocao";
import { BRAND } from "../../brands";
import {
    formStyles,
    titleStyles,
    introStyles,
    cardsContainerStyles,
    cardStyles,
    cardTitleStyles,
    janelaPromocaoStyles,
    voltarPromocoesStyles
} from "../styles/PreEngagementFormPhase.styles";

/*
 * PromocoesStep — a lista de promoções e o texto de cada uma, DENTRO do chat.
 *
 * É a primeira tela do widget que navega por dentro em vez de abrir uma aba: o card "Promoções
 * Ativas" leva aqui, e daqui se abre uma promoção. O apostador não sai do atendimento.
 *
 * ⚠️ O corpo é injetado como HTML, e ele já vem sanitizado do SERVIDOR — mesma decisão dos artigos
 * da Central, onde o `publicHelpCenter` injeta o body depois de passar pela allowlist. Não há um
 * segundo sanitizador aqui de propósito: seria uma régua paralela, e a biblioteca do servidor é de
 * Node — trazê-la ao bundle reproduziria a falha de 28/07, que tirou o webchat do ar por 1h20.
 *
 * A busca é SOB DEMANDA (monta ao entrar na tela), nunca no load do chat: quem não toca no card
 * não paga o payload.
 */

type Estado = "carregando" | "pronto" | "erro";

export const PromocoesStep = ({ onVoltar }: { onVoltar: () => void }) => {
    const [estado, setEstado] = useState<Estado>("carregando");
    const [promocoes, setPromocoes] = useState<PromocaoDoWidget[]>([]);
    const [textos, setTextos] = useState<TextosDoWidget | null>(null);
    const [abertaId, setAbertaId] = useState<string | null>(null);

    useEffect(() => {
        let vivo = true;
        getPromocoes(BRAND.slug).then((r) => {
            if (!vivo) return;
            setPromocoes(r.promocoes);
            setTextos(r.textos);
            setEstado(r.erro ? "erro" : "pronto");
        });
        return () => {
            vivo = false;
        };
    }, []);

    const aberta = promocoes.find((p) => p.id === abertaId) ?? null;

    // Dentro de uma promoção: o Voltar leva à lista, não à tela inicial.
    if (aberta) {
        return (
            <Box {...formStyles} data-test="promocoes-detalhe">
                <Box
                    {...voltarPromocoesStyles}
                    as="button"
                    type="button"
                    onClick={() => setAbertaId(null)}
                    data-test="promocoes-voltar-lista"
                >
                    <Text as="span" aria-hidden="true">
                        ←
                    </Text>
                    <Text as="span">{textos?.cardTitle ?? "Promoções"}</Text>
                </Box>
                <Text {...titleStyles} as="h3">
                    {aberta.title}
                </Text>
                {/* Embaixo do título e ANTES do texto: o prazo é lido junto com o nome da oferta,
                    não no fim de um corpo longo que o apostador talvez não role até o fim. */}
                {janelaParaApostador(aberta.startsAt, aberta.expiresAt) ? (
                    <Text {...janelaPromocaoStyles} as="p" data-test="promocao-janela-detalhe">
                        {janelaParaApostador(aberta.startsAt, aberta.expiresAt)}
                    </Text>
                ) : null}
                {aberta.bodyHtml ? (
                    <Box
                        className="promo-body"
                        data-test="promocoes-corpo"
                        dangerouslySetInnerHTML={{ __html: aberta.bodyHtml }}
                    />
                ) : null}
            </Box>
        );
    }

    return (
        <Box {...formStyles} data-test="promocoes-lista">
            <Box
                {...voltarPromocoesStyles}
                as="button"
                type="button"
                onClick={onVoltar}
                data-test="promocoes-voltar"
            >
                <Text as="span" aria-hidden="true">
                    ←
                </Text>
                <Text as="span">Voltar</Text>
            </Box>
            <Text {...titleStyles} as="h3">
                {textos?.cardTitle ?? "Promoções"}
            </Text>

            {estado === "carregando" && (
                <Text {...introStyles} as="p">
                    Carregando…
                </Text>
            )}

            {/*
              Falha de busca NÃO pode virar "não há promoções": são coisas diferentes, e confundir
              as duas faz o apostador achar que a casa não tem oferta nenhuma. Por isso o erro tem
              texto próprio.
            */}
            {estado === "erro" && (
                <Text {...introStyles} as="p" data-test="promocoes-erro">
                    Não consegui carregar agora. Tente de novo em instantes.
                </Text>
            )}

            {estado === "pronto" && promocoes.length === 0 && (
                <Text {...introStyles} as="p" data-test="promocoes-vazio">
                    {textos?.emptyMessage}
                </Text>
            )}

            {promocoes.length > 0 && (
                <Box {...cardsContainerStyles}>
                    {promocoes.map((p) => (
                        <Box
                            key={p.id}
                            as="button"
                            type="button"
                            onClick={() => setAbertaId(p.id)}
                            data-test={`promocao-${p.id}`}
                            {...cardStyles}
                        >
                            <Text as="span" {...cardTitleStyles}>
                                {p.title}
                            </Text>
                            {janelaParaApostador(p.startsAt, p.expiresAt) ? (
                                <Text
                                    as="span"
                                    {...janelaPromocaoStyles}
                                    data-test={`promocao-janela-${p.id}`}
                                >
                                    {janelaParaApostador(p.startsAt, p.expiresAt)}
                                </Text>
                            ) : null}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
